import React, { useState, useEffect, useRef } from "react";
import { Backdrop, CircularProgress, Button } from "@mui/material";
import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AADHeader from "./components/AADHeader.js";
import { InputText } from "primereact/inputtext";
import "./table.css";
import { Alert } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import RepositoryAddLink from "./RepositoryAddLink.js";
import usePermissions from "./hooks/usePermissions";
import { PERMISSIONS } from "./constants/permissions";

// Import reusable repository components
import {
  useRepositoryData,
  useRepositoryDelete,
  LinkCard,
  RepositoryTable,
  DeleteConfirmationModal,
} from "./components/repository";

const Repository = ({ isEmbedded = false, reportName = null, onBeforeAdd = null }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState("Delete File");
  const addLinkRef = useRef(null);

  const { has: hasPermission } = usePermissions();
  const canManageConfig = hasPermission(PERMISSIONS.MANAGE_CONFIGURATION);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Use custom hooks
  const {
    data,
    linkData,
    setLinkData,
    loading: dataLoading,
    error: apiError,
    setError: setApiError,
    refresh,
    fetchData,
  } = useRepositoryData({ reportName });

  const {
    showModal: displayConfirmationModal,
    itemToDelete: deletename,
    deleteMessage,
    confirmDelete: showDeleteModal,
    executeDelete: submitDelete,
    hideModal: hideConfirmationModal,
    loading: deleteLoading,
  } = useRepositoryDelete({ onSuccess: refresh });

  const loading = dataLoading || deleteLoading;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Note: useRepositoryData hook auto-fetches when reportName changes

  const renderHeader1 = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const handleTableRowDelete = (rowData) => {
    setDeleteTitle("Delete File");
    showDeleteModal(rowData.Name);
  };

  // Filtered links based on search term
  const filteredLinks = linkData.filter((link) => {
    const name = (link.Name || "").toLowerCase();
    const category = (link.Category || "").toLowerCase();
    const url = (link.Link || link["File Link"] || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || category.includes(term) || url.includes(term);
  });

  const displayedLinks = filteredLinks;
  const displayedFileRows = Array.isArray(data) ? data : [];

  const deleteLink = (targetLink) => {
    // Route through the shared delete flow so the scraped-link blob + registry entry
    // are removed on the backend (delete-repository-file-new by blob_name).
    if (targetLink?.blob_name) {
      setDeleteTitle("Delete Link");
      showDeleteModal(targetLink.blob_name, "Are you sure you want to delete this link?");
    }
  };

  const handleAddLinkSuccess = (newEntry) => {
    if (newEntry) {
      setLinkData((prev) => [
        ...prev,
        { Name: newEntry.linkName, Category: newEntry.linkCategory, Link: newEntry.url },
      ]);
    }
    fetchData();
  };

  const handleSubmitClick = () => {
    if (addLinkRef.current) {
      addLinkRef.current.submit();
    }
  };

  const repositoryContent = (
    <div className={`container ${!isEmbedded ? "formpd" : ""} repository-wrapper`}>
      {!isEmbedded && (
        <div className="flex">
          <div className="col-md-12 pd-0">
            <div className="steps__containt-hader">
              <h3 className="title">Repository</h3>
              <div role="presentation">
                <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon fontSize="small" />}>
                  <Typography className="breadcrumbs-font" color="inherit">
                    Company
                  </Typography>
                  <Typography color="text.primary">Repository</Typography>
                </Breadcrumbs>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shared controls strip — sits above FILES + LINKS panels */}
      <div className="row gx-5 repository-controls-row mb-2">
        <div className="col-md-12 d-flex justify-content-end">
          <Button
            className="refresh-cta"
            size="small"
            onClick={() => {
              if (onBeforeAdd && !onBeforeAdd()) return;
              setShowAddModal(true);
            }}
          >
            + Add More
          </Button>
        </div>
      </div>

      <div className="row gx-5 repository-panels-row">
        <div className="col-md-8 repository-files-col">
          {apiError && (
            <Alert variant="danger" onClose={() => setApiError(null)} dismissible>
              {apiError}
            </Alert>
          )}
          <div className="col-md-12 pd-0">
            <h2 className="h2-title repository-files-title mb-0">
              Files
              {dataLoading && (
                <span style={{ display: "inline-flex", alignItems: "center", marginLeft: "8px", position: "relative", top: "2px" }}>
                  <CircularProgress size={14} thickness={5} sx={{ color: "#407775" }} />
                </span>
              )}
            </h2>
            <div className="repository-files-divider" style={{ marginTop: "10px" }} />
          </div>

          <div className="data-responsive repository-files-scroll">
            <RepositoryTable
              data={displayedFileRows}
              onDelete={handleTableRowDelete}
              emptyMessage="No records added."
            />
          </div>
        </div>
        <div className="col-md-4 repository-links-col">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="h2-title mb-0">Links</h2>
            <span className="p-input-icon-left" style={{ width: "72%" }}>
              <img src="assets/img/search.png" className="search-i" alt="search" />
              <InputText value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search" className="wd-100" />
            </span>
          </div>
          <div className="links-wrapper repository-links-scroll">
            {displayedLinks.length > 0 ? (
              displayedLinks.map((link, index) => (
                <LinkCard
                  key={`${link.Name}-${link.DateTime || index}`}
                  link={link}
                  onDelete={deleteLink}
                  showEdit={false}
                  canDelete={canManageConfig}
                />
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
                <p>No links added yet</p>
                <p style={{ fontSize: '14px', color: '#999' }}>Add links to reference external resources</p>
              </div>
            )}
          </div>
        </div>
        <DeleteConfirmationModal
          show={displayConfirmationModal}
          onHide={hideConfirmationModal}
          onConfirm={submitDelete}
          itemName={deletename}
          message={deleteMessage}
          title={deleteTitle}
          variant="delete"
          loading={deleteLoading}
        />
      </div>

      <div>
        {showAddModal && (
          <Modal show={showAddModal} onHide={() => setShowAddModal(false)} className="custom-modal-width business-modal">
            <Modal.Header>
              <div className="steps__containt-hader edit-form">
                <h3 className="title">Add Files or Links</h3>
              </div>
            </Modal.Header>
            <Modal.Body>
              <RepositoryAddLink ref={addLinkRef} onSuccess={handleAddLinkSuccess} onClose={() => setShowAddModal(false)} existingLinks={linkData} />
            </Modal.Body>
            <Modal.Footer>
              <div className="edit-form forms-buttons-container">
                <Button onClick={() => setShowAddModal(false)} className="outline-btn">
                  Cancel
                </Button>
                <Button onClick={handleSubmitClick} className="next-button" style={{ color: "#fff!important" }}>
                  Submit
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>

    </div>
  );

  if (isEmbedded) {
    return repositoryContent;
  }

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader1()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        {repositoryContent}
      </div>
    </div>
  );
};

export default Repository;
